import React, { useState, useEffect } from "react";
import "./App.css";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Badge } from "./components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "./components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Textarea } from "./components/ui/textarea";
import { Calendar } from "./components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { CalendarIcon, PlusIcon, UserIcon, ClockIcon, CheckIcon, LogOutIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon, PencilIcon, ChevronUpIcon, FolderIcon, SearchIcon } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";

// Extracted Components
import { WorkStepTimeTracker } from "./components/worksteps";
import { UserTable, ArchiveTable, AssignmentTable } from "./components/admin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(false);

  // Auth states
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });

  // Admin states  
  const [tasks, setTasks] = useState([]);
  const [archivedTasks, setArchivedTasks] = useState([]);  // New: Archived tasks
  const [nextWorkSteps, setNextWorkSteps] = useState({});  // Next work steps by assignment ID
  const [previousWorkSteps, setPreviousWorkSteps] = useState({});  // Previous work steps by assignment ID
  const [users, setUsers] = useState([]);
  const [expandedTasks, setExpandedTasks] = useState({}); 
  const [expandedUsers, setExpandedUsers] = useState({});
  const [userActivities, setUserActivities] = useState({});
  const [taskTimeDetails, setTaskTimeDetails] = useState({});
  const [historicalTimeEntries, setHistoricalTimeEntries] = useState({});
  const [showHistoricalDialog, setShowHistoricalDialog] = useState(false);
  const [selectedTaskForHistorical, setSelectedTaskForHistorical] = useState(null);
  
  // Edit Assignment Dialog State
  const [showEditAssignmentDialog, setShowEditAssignmentDialog] = useState(false);
  const [selectedAssignmentForEdit, setSelectedAssignmentForEdit] = useState(null);
  const [editAssignmentForm, setEditAssignmentForm] = useState({
    status: '',
    completion_notes: '',
    notes: ''
  });

  // Edit Time Entry Dialog State
  const [showEditTimeEntryDialog, setShowEditTimeEntryDialog] = useState(false);
  const [selectedTimeEntryForEdit, setSelectedTimeEntryForEdit] = useState(null);
  const [editTimeEntryForm, setEditTimeEntryForm] = useState({
    start_time: '',
    end_time: '',
    actual_quantity: '',
    work_notes: '',
    setup_time_minutes: '0'
  });
  const [workStepTypes, setWorkStepTypes] = useState([]);
  const [workStepAssignments, setWorkStepAssignments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');  // User search functionality
  
  // Admin search states for different tabs
  const [taskSearchTerm, setTaskSearchTerm] = useState('');  // Tasks tab search
  
  const [newTaskForm, setNewTaskForm] = useState({ 
    title: '', 
    work_instruction: '', 
    article_number: '', // New field: Artikelnummer
    due_date: new Date(), 
    target_quantity: 0,
    work_steps: []
  });

  // User states
  const [taskEntries, setTaskEntries] = useState({});
  const [newEntryForm, setNewEntryForm] = useState({
    entry_date: new Date(),
    start_time: new Date(),
    end_time: new Date(),
    actual_quantity: 0,
    notes: ''
  });

  // Dialog states
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showAssignWorkStep, setShowAssignWorkStep] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedWorkStep, setSelectedWorkStep] = useState(null);
  const [editTaskForm, setEditTaskForm] = useState({
    title: '',
    work_instruction: '',
    article_number: '', // New field: Artikelnummer
    due_date: new Date(),
    target_quantity: 0,
    work_steps: []
  });

  useEffect(() => {
    if (token) {
      fetchUserProfile();
    }
  }, [token]);

  useEffect(() => {
    if (user) {
      fetchTasks();
      if (user.role === 'admin') {
        fetchUsers();
        fetchWorkStepTypes();
        fetchWorkStepAssignments();
        fetchArchivedTasks();  // Also load archived tasks for admins
      } else {
        fetchWorkStepAssignments(); // Users see their own assignments
      }
    }
  }, [user]);

  // Load next work steps when work step assignments change
  useEffect(() => {
    if (workStepAssignments.length > 0) {
      fetchNextWorkSteps();
    }
  }, [workStepAssignments]);

  const fetchUserProfile = async () => {
    try {
      // Decode JWT token to get user info
      const decoded = JSON.parse(atob(token.split('.')[1]));
      
      // If user is admin, fetch all users to get complete profile
      if (decoded.role === 'admin') {
        try {
          const response = await axios.get(`${API}/users`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const currentUser = response.data.find(u => u.username === decoded.sub);
          if (currentUser) {
            setUser(currentUser);
            return;
          }
        } catch (error) {
          console.error('Failed to fetch admin user list:', error);
        }
      }
      
      // For regular users or if admin user fetch failed, construct user from token
      setUser({
        id: decoded.user_id,
        username: decoded.sub,
        role: decoded.role || 'user',
        full_name: decoded.sub // Will be overridden if we have better data
      });
      
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      handleLogout();
    }
  };

  const fetchWorkStepTypes = async () => {
    try {
      const response = await axios.get(`${API}/work-step-types`);
      setWorkStepTypes(response.data.types);
    } catch (error) {
      console.error('Failed to fetch work step types:', error);
    }
  };

  const fetchNextWorkSteps = async () => {
    try {
      const nextStepsData = {};
      const previousStepsData = {};
      for (const assignment of workStepAssignments) {
        try {
          // Fetch next step
          const nextResponse = await axios.get(`${API}/work-step-assignments/${assignment.id}/next-step`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (nextResponse.data.next_step) {
            nextStepsData[assignment.id] = nextResponse.data.next_step;
          }
          
          // Fetch previous step
          const prevResponse = await axios.get(`${API}/work-step-assignments/${assignment.id}/previous-step`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          previousStepsData[assignment.id] = prevResponse.data;
        } catch (error) {
          console.log(`Error fetching step info for assignment ${assignment.id}`);
        }
      }
      setNextWorkSteps(nextStepsData);
      setPreviousWorkSteps(previousStepsData);
    } catch (error) {
      console.error('Failed to fetch work steps info:', error);
    }
  };

  const getNextWorkStep = (currentAssignment) => {
    return nextWorkSteps[currentAssignment.id] || null;
  };

  // Check if a work step can be started (previous step must be completed)
  const canStartWorkStep = (assignment) => {
    const prevStepInfo = previousWorkSteps[assignment.id];
    if (!prevStepInfo) return true; // No info yet, assume can start
    return prevStepInfo.can_start || prevStepInfo.is_first_step;
  };

  // Get the status color class for a work step card
  const getWorkStepCardClass = (assignment) => {
    // In Bearbeitung or Pausiert = Yellow (active work)
    if (assignment.status === 'In Bearbeitung' || assignment.status === 'Pausiert') {
      return 'border-l-8 border-l-yellow-500 bg-yellow-50 shadow-md';
    }
    // Completed = Gray
    if (assignment.status === 'Erledigt') {
      return 'border-l-8 border-l-gray-400 bg-gray-50';
    }
    // Check if can start
    if (canStartWorkStep(assignment)) {
      return 'border-l-8 border-l-green-500 bg-green-50 shadow-md'; // Can start = Green
    }
    return 'border-l-8 border-l-red-500 bg-red-50 shadow-md'; // Cannot start = Red
  };

  const getFilteredWorkStepAssignments = () => {
    if (!searchTerm.trim()) {
      return workStepAssignments.filter(assignment => assignment.user_id === user.id);
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return workStepAssignments.filter(assignment => {
      if (assignment.user_id !== user.id) return false;
      
      const task = assignment.task;
      if (!task) return false;

      // Search in task title (Auftrag), work instruction (Bezeichnung), and article number
      const matchesTitle = task.title?.toLowerCase().includes(searchLower);
      const matchesWorkInstruction = task.work_instruction?.toLowerCase().includes(searchLower);
      const matchesArticleNumber = task.article_number?.toLowerCase().includes(searchLower);
      const matchesWorkStepType = assignment.work_step?.step_type?.toLowerCase().includes(searchLower);

      return matchesTitle || matchesWorkInstruction || matchesArticleNumber || matchesWorkStepType;
    });
  };

  // Admin search filters
  const getFilteredTasks = () => {
    if (!taskSearchTerm.trim()) {
      return tasks;
    }

    const searchLower = taskSearchTerm.toLowerCase().trim();
    return tasks.filter(task => {
      const matchesTitle = task.title?.toLowerCase().includes(searchLower);
      const matchesWorkInstruction = task.work_instruction?.toLowerCase().includes(searchLower);
      const matchesArticleNumber = task.article_number?.toLowerCase().includes(searchLower);
      const matchesStatus = task.status?.toLowerCase().includes(searchLower);

      return matchesTitle || matchesWorkInstruction || matchesArticleNumber || matchesStatus;
    });
  };

  const toggleTaskDetails = async (taskId) => {
    const isExpanded = expandedTasks[taskId];
    
    if (isExpanded) {
      // Collapse
      setExpandedTasks(prev => ({ ...prev, [taskId]: false }));
    } else {
      // Expand and load time details
      setExpandedTasks(prev => ({ ...prev, [taskId]: true }));
      
      if (!taskTimeDetails[taskId]) {
        try {
          const response = await axios.get(`${API}/tasks/${taskId}/time-summary`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          setTaskTimeDetails(prev => ({ ...prev, [taskId]: response.data }));
        } catch (error) {
          console.error('Failed to fetch task time details:', error);
          setTaskTimeDetails(prev => ({ 
            ...prev, 
            [taskId]: { error: 'Fehler beim Laden der Zeitdetails' }
          }));
        }
      }
    }
  };

  const fetchWorkStepAssignments = async () => {
    try {
      const response = await axios.get(`${API}/work-step-assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkStepAssignments(response.data);
    } catch (error) {
      console.error('Failed to fetch work step assignments:', error);
    }
  };

  const fetchArchivedTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch archived tasks:', error);
      toast.error('Fehler beim Laden der archivierten Aufgaben');
    }
  };

  const archiveTask = async (taskId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Aufgabe archivieren möchten?')) {
      return;
    }

    try {
      await axios.put(`${API}/tasks/${taskId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTasks();
      fetchArchivedTasks();
      toast.success('Aufgabe erfolgreich archiviert');
    } catch (error) {
      console.error('Failed to archive task:', error);
      toast.error('Fehler beim Archivieren der Aufgabe');
    }
  };

  const restoreTaskFromArchive = async (taskId) => {
    if (!window.confirm('Möchten Sie eine neue Aufgabe basierend auf dieser Vorlage erstellen?')) {
      return;
    }

    try {
      await axios.put(`${API}/tasks/${taskId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTasks();
      toast.success('Neue Aufgabe basierend auf Archiv-Vorlage erstellt');
    } catch (error) {
      console.error('Failed to restore task from archive:', error);
      toast.error('Fehler beim Wiederherstellen der Aufgabe');
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      toast.error('Fehler beim Laden der Aufgaben');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    }
  };

  const fetchTaskEntries = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTaskEntries(prev => ({ ...prev, [taskId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch task entries:', error);
      toast.error('Fehler beim Laden der Einträge');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      const { access_token, user: userData } = response.data;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('token', access_token);
      
      toast.success('Erfolgreich angemeldet');
    } catch (error) {
      console.error('Login failed:', error);
      toast.error('Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    setLoginForm({ username: '', password: '' });
  };

  // User management functions (used by UserTable component)
  const createUser = async (formData) => {
    try {
      await axios.post(`${API}/auth/register`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchUsers();
      toast.success('Benutzer erfolgreich erstellt');
      return true;
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error('Fehler beim Erstellen des Benutzers');
      return false;
    }
  };

  const editUser = async (userId, updateData, userName) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Benutzer "${userName}" erfolgreich aktualisiert`);
      fetchUsers();
      return true;
    } catch (error) {
      console.error('Failed to update user:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data?.detail || 'Benutzername bereits vergeben');
      } else {
        toast.error('Fehler beim Aktualisieren des Benutzers');
      }
      return false;
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie den Benutzer "${userName}" löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden. Alle zugehörigen Daten (Zuweisungen, Zeiteinträge) werden ebenfalls gelöscht.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchUsers();
      toast.success(`Benutzer "${userName}" erfolgreich gelöscht`);
    } catch (error) {
      console.error('Failed to delete user:', error);
      if (error.response?.status === 400) {
        toast.error('Sie können Ihr eigenes Konto nicht löschen');
      } else {
        toast.error('Fehler beim Löschen des Benutzers');
      }
    }
  };

  const editTask = async (task) => {
    try {
      // Get the current work plan for this task
      let currentWorkPlan = null;
      try {
        const workPlanResponse = await axios.get(`${API}/tasks/${task.id}/work-plan`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        currentWorkPlan = workPlanResponse.data;
      } catch (error) {
        // No work plan exists, that's ok
        console.log('No work plan found for task');
      }

      // Set up the edit form
      setEditTaskForm({
        title: task.title,
        work_instruction: task.work_instruction,
        article_number: task.article_number || '', // Handle existing tasks without article_number
        due_date: new Date(task.due_date),
        target_quantity: task.target_quantity,
        work_steps: currentWorkPlan ? currentWorkPlan.work_steps.sort((a, b) => a.order - b.order) : []
      });
      setSelectedTask(task);
      setShowEditTask(true);
    } catch (error) {
      console.error('Failed to prepare task edit:', error);
      toast.error('Fehler beim Vorbereiten der Aufgaben-Bearbeitung');
    }
  };

  const updateTask = async (e) => {
    e.preventDefault();
    try {
      // Update the basic task information
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        title: editTaskForm.title,
        work_instruction: editTaskForm.work_instruction,
        article_number: editTaskForm.article_number,
        due_date: editTaskForm.due_date,
        target_quantity: editTaskForm.target_quantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update work plan if there are work steps
      if (editTaskForm.work_steps && editTaskForm.work_steps.length > 0) {
        // Prepare work steps for API - PRESERVE existing IDs to maintain assignments
        const workStepsForApi = editTaskForm.work_steps.map((step, index) => ({
          step_type: step.step_type,
          order: index + 1,  // Ensure correct order
          notes: step.notes || '',
          // CRITICAL: Preserve the original ID if it exists to maintain work step assignments
          ...(step.id && { id: step.id })
        }));

        try {
          // Try to update existing work plan
          await axios.put(`${API}/tasks/${selectedTask.id}/work-plan`, {
            work_steps: workStepsForApi
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (workPlanError) {
          if (workPlanError.response?.status === 404) {
            // Work plan doesn't exist, create it
            await axios.post(`${API}/tasks/${selectedTask.id}/work-plan`, {
              work_steps: workStepsForApi
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
          } else {
            throw workPlanError;
          }
        }
      }

      setEditTaskForm({
        title: '',
        work_instruction: '',
        article_number: '',
        due_date: new Date(),
        target_quantity: 0,
        work_steps: []
      });
      setShowEditTask(false);
      setSelectedTask(null);
      fetchTasks();
      toast.success('Aufgabe erfolgreich aktualisiert');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Fehler beim Aktualisieren der Aufgabe');
    }
  };

  const addWorkStepToEdit = () => {
    const newStep = {
      step_type: workStepTypes[0] || 'lasern',
      order: editTaskForm.work_steps.length + 1,
      notes: ''
    };
    setEditTaskForm(prev => ({
      ...prev,
      work_steps: [...prev.work_steps, newStep]
    }));
  };

  const removeWorkStepFromEdit = (index) => {
    setEditTaskForm(prev => ({
      ...prev,
      work_steps: prev.work_steps.filter((_, i) => i !== index)
    }));
  };

  const updateWorkStepInEdit = (index, field, value) => {
    setEditTaskForm(prev => ({
      ...prev,
      work_steps: prev.work_steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const createTask = async (e) => {
    e.preventDefault();
    try {
      // First create the task
      const taskResponse = await axios.post(`${API}/tasks`, {
        title: newTaskForm.title,
        work_instruction: newTaskForm.work_instruction,
        article_number: newTaskForm.article_number,
        due_date: newTaskForm.due_date,
        target_quantity: newTaskForm.target_quantity
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newTask = taskResponse.data;
      console.log('Task created:', newTask);
      console.log('Work steps to create:', newTaskForm.work_steps);
      
      // If work steps are defined, create work plan
      if (newTaskForm.work_steps && newTaskForm.work_steps.length > 0) {
        try {
          const workPlanResponse = await axios.post(`${API}/tasks/${newTask.id}/work-plan`, {
            work_steps: newTaskForm.work_steps
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });
          console.log('Work plan created:', workPlanResponse.data);
          toast.success('Aufgabe mit Arbeitsplan erfolgreich erstellt');
        } catch (workPlanError) {
          console.error('Failed to create work plan:', workPlanError);
          toast.error('Aufgabe erstellt, aber Arbeitsplan-Erstellung fehlgeschlagen');
        }
      } else {
        toast.success('Aufgabe erfolgreich erstellt');
      }
      
      setNewTaskForm({ 
        title: '', 
        work_instruction: '', 
        article_number: '',
        due_date: new Date(), 
        target_quantity: 0,
        work_steps: []
      });
      setShowCreateTask(false);
      fetchTasks();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Fehler beim Erstellen der Aufgabe');
    }
  };

  const assignWorkStep = async (workStepId, userId) => {
    try {
      await axios.post(`${API}/work-step-assignments`, {
        work_step_id: workStepId,
        user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh work step assignments in the main view
      fetchWorkStepAssignments();
      toast.success('Arbeitsschritt erfolgreich zugewiesen');
    } catch (error) {
      console.error('Failed to assign work step:', error);
      if (error.response?.status === 400 && error.response.data?.detail?.includes('already assigned')) {
        toast.error('Dieser Arbeitsschritt ist bereits zugewiesen');
      } else {
        toast.error('Fehler beim Zuweisen des Arbeitsschritts');
      }
    }
  };

  const removeWorkStepAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API}/work-step-assignments/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchWorkStepAssignments();
      toast.success('Arbeitsschritt-Zuweisung entfernt');
    } catch (error) {
      console.error('Failed to remove work step assignment:', error);
      toast.error('Fehler beim Entfernen der Zuweisung');
    }
  };

  const getUserActiveWorkStep = (userId) => {
    // Find if user has any work step in "In Bearbeitung" status
    return workStepAssignments.find(assignment => 
      assignment.user_id === userId && assignment.status === 'In Bearbeitung'
    );
  };

  const refreshTaskTimeDetails = async () => {
    // Update task time details for all expanded tasks
    for (const taskId of Object.keys(expandedTasks)) {
      if (expandedTasks[taskId] && taskTimeDetails[taskId]) {
        try {
          const response = await axios.get(`${API}/tasks/${taskId}/time-summary`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTaskTimeDetails(prev => ({ ...prev, [taskId]: response.data }));
        } catch (error) {
          console.error(`Failed to refresh time details for task ${taskId}:`, error);
        }
      }
    }
  };

  const startWorkStep = async (assignmentId, startData) => {
    try {
      await axios.put(`${API}/work-step-assignments/${assignmentId}/start`, startData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchWorkStepAssignments();
      await refreshTaskTimeDetails(); // Refresh time details after starting work step
      toast.success('Arbeitsschritt gestartet');
    } catch (error) {
      console.error('Failed to start work step:', error);
      if (error.response?.status === 400) {
        toast.error('Arbeitsschritt kann in diesem Status nicht gestartet werden');
      } else {
        toast.error('Fehler beim Starten des Arbeitsschritts');
      }
    }
  };

  const pauseWorkStep = async (assignmentId, pauseData) => {
    try {
      await axios.put(`${API}/work-step-assignments/${assignmentId}/pause`, pauseData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchWorkStepAssignments();
      await refreshTaskTimeDetails(); // Refresh time details after pausing work step
      toast.success('Arbeitsschritt pausiert');
    } catch (error) {
      console.error('Failed to pause work step:', error);
      if (error.response?.status === 400) {
        toast.error('Endzeit muss nach Startzeit liegen');
      } else {
        toast.error('Fehler beim Pausieren des Arbeitsschritts');
      }
    }
  };

  const fetchWorkTimeEntries = async (assignmentId) => {
    try {
      const response = await axios.get(`${API}/work-step-assignments/${assignmentId}/time-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch work time entries:', error);
      return [];
    }
  };

  const openEditAssignmentDialog = (assignment) => {
    setSelectedAssignmentForEdit(assignment);
    setEditAssignmentForm({
      status: assignment.status || '',
      completion_notes: assignment.completion_notes || '',
      notes: assignment.notes || ''
    });
    setShowEditAssignmentDialog(true);
  };

  const updateWorkStepAssignment = async (assignmentId, updateData) => {
    try {
      await axios.put(`${API}/admin/work-step-assignments/${assignmentId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchWorkStepAssignments();
      toast.success('Arbeitsschritt-Zuweisung erfolgreich aktualisiert');
    } catch (error) {
      console.error('Failed to update work step assignment:', error);
      toast.error('Fehler beim Aktualisieren der Zuweisung');
    }
  };

  const handleEditAssignmentSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare update data (only send non-empty values)
    const updateData = {};
    
    if (editAssignmentForm.status && editAssignmentForm.status !== selectedAssignmentForEdit.status) {
      updateData.status = editAssignmentForm.status;
      
      // Set timestamps based on status changes
      if (editAssignmentForm.status === 'In Bearbeitung' && !selectedAssignmentForEdit.started_at) {
        updateData.started_at = new Date().toISOString();
      } else if (editAssignmentForm.status === 'Erledigt' && !selectedAssignmentForEdit.completed_at) {
        updateData.completed_at = new Date().toISOString();
      }
    }
    
    if (editAssignmentForm.completion_notes !== (selectedAssignmentForEdit.completion_notes || '')) {
      updateData.completion_notes = editAssignmentForm.completion_notes;
    }
    
    if (editAssignmentForm.notes !== (selectedAssignmentForEdit.notes || '')) {
      updateData.notes = editAssignmentForm.notes;
    }
    
    if (Object.keys(updateData).length === 0) {
      toast.info('Keine Änderungen vorgenommen');
      setShowEditAssignmentDialog(false);
      return;
    }
    
    await updateWorkStepAssignment(selectedAssignmentForEdit.id, updateData);
    setShowEditAssignmentDialog(false);
  };

  const openEditTimeEntryDialog = (timeEntry) => {
    setSelectedTimeEntryForEdit(timeEntry);
    
    // Format time values for input fields
    const startTime = timeEntry.start_time ? format(new Date(timeEntry.start_time), "HH:mm") : '';
    const endTime = timeEntry.end_time ? format(new Date(timeEntry.end_time), "HH:mm") : '';
    
    setEditTimeEntryForm({
      start_time: startTime,
      end_time: endTime,
      actual_quantity: timeEntry.actual_quantity || '',
      work_notes: timeEntry.work_notes || '',
      setup_time_minutes: timeEntry.setup_time_minutes || '0'
    });
    setShowEditTimeEntryDialog(true);
  };

  const updateWorkTimeEntry = async (entryId, updateData) => {
    try {
      await axios.put(`${API}/admin/work-time-entries/${entryId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh historical time entries for the current task
      if (selectedTaskForHistorical) {
        await fetchHistoricalTimeEntries(selectedTaskForHistorical);
      }
      
      toast.success('Zeiteintrag erfolgreich aktualisiert');
    } catch (error) {
      console.error('Failed to update work time entry:', error);
      toast.error('Fehler beim Aktualisieren des Zeiteintrags');
    }
  };

  const handleEditTimeEntrySubmit = async (e) => {
    e.preventDefault();
    
    // Prepare update data
    const updateData = {};
    
    // Convert time strings to ISO datetime if changed
    if (editTimeEntryForm.start_time !== (selectedTimeEntryForEdit.start_time ? format(new Date(selectedTimeEntryForEdit.start_time), "HH:mm") : '')) {
      const [hours, minutes] = editTimeEntryForm.start_time.split(':');
      const startDate = selectedTimeEntryForEdit.start_time ? new Date(selectedTimeEntryForEdit.start_time) : new Date();
      startDate.setHours(parseInt(hours), parseInt(minutes), 0);
      updateData.start_time = startDate.toISOString();
    }
    
    if (editTimeEntryForm.end_time !== (selectedTimeEntryForEdit.end_time ? format(new Date(selectedTimeEntryForEdit.end_time), "HH:mm") : '')) {
      const [hours, minutes] = editTimeEntryForm.end_time.split(':');
      const endDate = selectedTimeEntryForEdit.end_time ? new Date(selectedTimeEntryForEdit.end_time) : new Date();
      endDate.setHours(parseInt(hours), parseInt(minutes), 0);
      updateData.end_time = endDate.toISOString();
    }
    
    if (editTimeEntryForm.actual_quantity !== (selectedTimeEntryForEdit.actual_quantity || '')) {
      updateData.actual_quantity = editTimeEntryForm.actual_quantity ? parseInt(editTimeEntryForm.actual_quantity) : null;
    }
    
    if (editTimeEntryForm.work_notes !== (selectedTimeEntryForEdit.work_notes || '')) {
      updateData.work_notes = editTimeEntryForm.work_notes;
    }
    
    if (editTimeEntryForm.setup_time_minutes !== (selectedTimeEntryForEdit.setup_time_minutes || '0')) {
      updateData.setup_time_minutes = editTimeEntryForm.setup_time_minutes ? parseInt(editTimeEntryForm.setup_time_minutes) : null;
    }
    
    if (Object.keys(updateData).length === 0) {
      toast.info('Keine Änderungen vorgenommen');
      setShowEditTimeEntryDialog(false);
      return;
    }
    
    await updateWorkTimeEntry(selectedTimeEntryForEdit.id, updateData);
    setShowEditTimeEntryDialog(false);
  };

  const fetchHistoricalTimeEntries = async (taskId) => {
    try {
      const response = await axios.get(`${API}/tasks/${taskId}/historical-time-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Transform the data to match our expected structure
      const transformedData = [];
      const entriesByWorkStep = {};
      
      // Group entries by work step
      response.data.forEach(entry => {
        const workStepId = entry.assignment ? entry.assignment.work_step_id : 'historical';
        const workStepType = entry.work_step ? entry.work_step.step_type : 'Gelöschter Arbeitsschritt';
        const userName = entry.user ? entry.user.full_name : 'Unbekannter Benutzer';
        const status = entry.assignment ? entry.assignment.status : 'Historisch';
        
        const key = `${workStepId}_${workStepType}_${userName}`;
        
        if (!entriesByWorkStep[key]) {
          entriesByWorkStep[key] = {
            step_type: workStepType,
            user_name: userName,
            status: status,
            time_entries: [],
            total_minutes: 0
          };
        }
        
        // Calculate duration
        let duration_minutes = 0;
        if (entry.start_time && entry.end_time) {
          const start = new Date(entry.start_time);
          const end = new Date(entry.end_time);
          duration_minutes = (end - start) / (1000 * 60);
        }
        
        entriesByWorkStep[key].time_entries.push({
          id: entry.id,  // Add the entry ID for editing
          start_time: entry.start_time,
          end_time: entry.end_time,
          duration_minutes: duration_minutes,
          notes: entry.work_notes || '',
          actual_quantity: entry.actual_quantity,
          work_notes: entry.work_notes || '',
          entry_type: entry.entry_type || 'work_session',
          is_historical: entry.is_historical || false,
          setup_time_minutes: entry.setup_time_minutes || 0  // Rüstzeit für Lasern
        });
        
        entriesByWorkStep[key].total_minutes += duration_minutes;
      });
      
      // Convert to array
      Object.values(entriesByWorkStep).forEach(workStep => {
        transformedData.push(workStep);
      });
      
      setHistoricalTimeEntries(prev => ({ ...prev, [taskId]: transformedData }));
      setSelectedTaskForHistorical(taskId);
      setShowHistoricalDialog(true);
    } catch (error) {
      console.error('Failed to fetch historical time entries:', error);
      toast.error('Fehler beim Laden der historischen Zeiteinträge');
    }
  };

  const addDirectTimeEntry = async (assignmentId, directTimeData) => {
    try {
      await axios.put(`${API}/work-step-assignments/${assignmentId}/add-direct-time`, directTimeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchWorkStepAssignments();
      await refreshTaskTimeDetails(); // Refresh time details after adding time entry
      toast.success('Zeiteintrag erfolgreich hinzugefügt');
    } catch (error) {
      console.error('Failed to add direct time entry:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Fehler beim Hinzufügen des Zeiteintrags');
      } else {
        toast.error('Fehler beim Hinzufügen des Zeiteintrags');
      }
    }
  };

  const completeWorkStepDirect = async (assignmentId, directTimeData) => {
    try {
      await axios.put(`${API}/work-step-assignments/${assignmentId}/complete-direct`, directTimeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchWorkStepAssignments();
      await refreshTaskTimeDetails(); // Refresh time details after completing work step
      toast.success('Arbeitsschritt mit direkter Zeiteingabe erfolgreich abgeschlossen');
    } catch (error) {
      console.error('Failed to complete work step with direct time:', error);
      if (error.response?.status === 400) {
        toast.error(error.response.data.detail || 'Direkte Zeiteingabe nur für Laser-Arbeitsschritte erlaubt');
      } else {
        toast.error('Fehler beim Abschließen des Arbeitsschritts mit direkter Zeiteingabe');
      }
    }
  };

  const completeWorkStep = async (assignmentId, workStepData) => {
    try {
      await axios.put(`${API}/work-step-assignments/${assignmentId}/complete`, workStepData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchWorkStepAssignments();
      await refreshTaskTimeDetails(); // Refresh time details after completing work step
      toast.success('Arbeitsschritt erfolgreich abgeschlossen');
    } catch (error) {
      console.error('Failed to complete work step:', error);
      if (error.response?.status === 400) {
        toast.error('Endzeit muss nach Startzeit liegen');
      } else {
        toast.error('Fehler beim Abschließen des Arbeitsschritts');
      }
    }
  };

  const addWorkStep = () => {
    const newStep = {
      step_type: workStepTypes[0] || 'lasern',
      order: newTaskForm.work_steps.length + 1,
      notes: ''
    };
    setNewTaskForm(prev => ({
      ...prev,
      work_steps: [...prev.work_steps, newStep]
    }));
    
    // Scroll to the new work step after it's added
    setTimeout(() => {
      const newIndex = newTaskForm.work_steps.length;
      const element = document.getElementById(`work-step-${newIndex}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const removeWorkStep = (index) => {
    setNewTaskForm(prev => ({
      ...prev,
      work_steps: prev.work_steps.filter((_, i) => i !== index)
    }));
  };

  const updateWorkStep = (index, field, value) => {
    setNewTaskForm(prev => ({
      ...prev,
      work_steps: prev.work_steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const assignTask = async (taskId, userId) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, {
        assigned_user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTasks();
      toast.success('Aufgabe erfolgreich zugewiesen');
    } catch (error) {
      console.error('Failed to assign task:', error);
      toast.error('Fehler beim Zuweisen der Aufgabe');
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTasks();
      toast.success('Aufgabenstatus aktualisiert');
    } catch (error) {
      console.error('Failed to update task status:', error);
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const addTaskEntry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tasks/${selectedTask}/entries`, newEntryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewEntryForm({
        entry_date: new Date(),
        start_time: new Date(),
        end_time: new Date(),
        actual_quantity: 0,
        notes: ''
      });
      setShowAddEntry(false);
      fetchTaskEntries(selectedTask);
      toast.success('Eintrag erfolgreich hinzugefügt');
    } catch (error) {
      console.error('Failed to add task entry:', error);
      toast.error('Fehler beim Hinzufügen des Eintrags');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diese Aufgabe löschen möchten? Alle zugehörigen Zeiteinträge werden auch gelöscht.')) {
      return;
    }

    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchTasks();
      fetchWorkStepAssignments(); // Also refresh work step assignments after task deletion
      toast.success('Aufgabe erfolgreich gelöscht');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Fehler beim Löschen der Aufgabe');
    }
  };

  const toggleTaskEntries = async (taskId) => {
    if (expandedTasks[taskId]) {
      // Collapse - remove from expanded tasks
      setExpandedTasks(prev => {
        const newState = { ...prev };
        delete newState[taskId];
        return newState;
      });
    } else {
      // Expand - fetch entries and add to expanded tasks
      try {
        const response = await axios.get(`${API}/tasks/${taskId}/entries`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTaskEntries(prev => ({ ...prev, [taskId]: response.data }));
        setExpandedTasks(prev => ({ ...prev, [taskId]: true }));
      } catch (error) {
        console.error('Failed to fetch task entries:', error);
        toast.error('Fehler beim Laden der Einträge');
      }
    }
  };

  const toggleUserActivities = async (userId) => {
    if (expandedUsers[userId]) {
      // Collapse - remove from expanded users
      setExpandedUsers(prev => {
        const newState = { ...prev };
        delete newState[userId];
        return newState;
      });
    } else {
      // Expand - fetch activities and add to expanded users
      try {
        const response = await axios.get(`${API}/users/${userId}/recent-activities`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserActivities(prev => ({ ...prev, [userId]: response.data }));
        setExpandedUsers(prev => ({ ...prev, [userId]: true }));
      } catch (error) {
        console.error('Failed to fetch user activities:', error);
        toast.error('Fehler beim Laden der Benutzeraktivitäten');
      }
    }
  };

  const deleteOldEntries = async (userId, userName) => {
    if (!window.confirm(`Sind Sie sicher, dass Sie alle Einträge älter als 5 Tage für ${userName} löschen möchten?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/users/${userId}/old-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh user activities if expanded
      if (expandedUsers[userId]) {
        toggleUserActivities(userId);
        setTimeout(() => toggleUserActivities(userId), 100);
      }
      
      toast.success(`${response.data.deleted_count} alte Einträge gelöscht`);
    } catch (error) {
      console.error('Failed to delete old entries:', error);
      toast.error('Fehler beim Löschen der alten Einträge');
    }
  };

  const getWorkStepBadgeClass = (stepType) => {
    const colorMap = {
      'programmieren': 'bg-purple-600 hover:bg-purple-700 text-white',
      'lasern': 'bg-red-600 hover:bg-red-700 text-white',
      'kanten': 'bg-orange-600 hover:bg-orange-700 text-white',
      'bohren': 'bg-yellow-600 hover:bg-yellow-700 text-white',
      'senken': 'bg-amber-600 hover:bg-amber-700 text-white',
      'drehen': 'bg-green-600 hover:bg-green-700 text-white',
      'fräsen': 'bg-emerald-600 hover:bg-emerald-700 text-white',
      'sägen': 'bg-teal-600 hover:bg-teal-700 text-white',
      'runden': 'bg-cyan-600 hover:bg-cyan-700 text-white',
      'entgraten': 'bg-indigo-600 hover:bg-indigo-700 text-white',
      'WIG-Schweißen': 'bg-violet-600 hover:bg-violet-700 text-white',
      'MAG-Schweißen': 'bg-pink-600 hover:bg-pink-700 text-white',
      'polieren': 'bg-rose-600 hover:bg-rose-700 text-white',
      'trowalisieren': 'bg-stone-600 hover:bg-stone-700 text-white',
      'Fremdbearbeitung': 'bg-gray-600 hover:bg-gray-700 text-white',
      'divers': 'bg-blue-600 hover:bg-blue-700 text-white'
    };
    
    return colorMap[stepType] || 'bg-blue-600 hover:bg-blue-700 text-white';
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Erstellt': 'bg-gray-100 text-gray-800',
      'Zugewiesen': 'bg-blue-100 text-blue-800',
      'In Bearbeitung': 'bg-yellow-100 text-yellow-800',
      'Pausiert': 'bg-orange-100 text-orange-800',
      'Erledigt': 'bg-green-100 text-green-800'
    };
    
    return (
      <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
        {status}
      </Badge>
    );
  };

  if (!token || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'transparent'}}>
        <Card className="w-full max-w-md login-card">
          <CardHeader>
            <CardTitle className="text-center">Aufgabenverwaltung</CardTitle>
            <CardDescription className="text-center">
              Melden Sie sich an, um fortzufahren
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username">Benutzername</Label>
                <Input
                  id="username"
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Anmelden...' : 'Anmelden'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: 'transparent'}}>
      <header className="bg-white shadow-sm border-b header-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl font-semibold text-gray-900">
              Aufgabenverwaltung
            </h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user.full_name} ({user.role === 'admin' ? 'Administrator' : 'Benutzer'})
              </span>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOutIcon className="h-4 w-4 mr-2" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {user.role === 'admin' ? (
          <Tabs defaultValue="tasks" className="space-y-4">
            <TabsList>
              <TabsTrigger value="tasks">Aufgaben</TabsTrigger>
              <TabsTrigger value="archive">Archiv</TabsTrigger>
              <TabsTrigger value="assignments">Arbeitsschritte</TabsTrigger>
              <TabsTrigger value="users">Benutzer</TabsTrigger>
            </TabsList>

            <TabsContent value="tasks" className="space-y-4">
              <div className="space-y-4">
                <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-center md:space-y-0">
                  <h2 className="text-2xl font-bold text-gray-900">Aufgaben verwalten</h2>
                  
                  <Dialog open={showCreateTask} onOpenChange={setShowCreateTask}>
                    <DialogTrigger asChild>
                      <Button>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Neue Aufgabe
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Neue Aufgabe erstellen</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={createTask} className="space-y-4">
                      <div>
                        <Label htmlFor="title">Auftrag</Label>
                        <Input
                          id="title"
                          value={newTaskForm.title}
                          onChange={(e) => setNewTaskForm(prev => ({ ...prev, title: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="work_instruction">Bezeichnung</Label>
                        <Textarea
                          id="work_instruction"
                          value={newTaskForm.work_instruction}
                          onChange={(e) => setNewTaskForm(prev => ({ ...prev, work_instruction: e.target.value }))}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="article_number">Artikelnummer</Label>
                        <Input
                          id="article_number"
                          type="text"
                          value={newTaskForm.article_number}
                          onChange={(e) => setNewTaskForm(prev => ({ ...prev, article_number: e.target.value }))}
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label>Liefertermin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(newTaskForm.due_date, "PPP", { locale: de })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={newTaskForm.due_date}
                              onSelect={(date) => setNewTaskForm(prev => ({ ...prev, due_date: date }))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label htmlFor="target_quantity">Sollstückzahl</Label>
                        <Input
                          id="target_quantity"
                          type="number"
                          value={newTaskForm.target_quantity}
                          onChange={(e) => setNewTaskForm(prev => ({ ...prev, target_quantity: parseInt(e.target.value) }))}
                          required
                        />
                      </div>
                      
                      {/* Work Plan Section */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <Label className="text-base font-medium">Arbeitsplan</Label>
                          <Button type="button" variant="outline" size="sm" onClick={addWorkStep}>
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Arbeitsschritt hinzufügen
                          </Button>
                        </div>
                        
                        <div className="max-h-60 overflow-y-auto space-y-3" id="work-steps-container">
                          {newTaskForm.work_steps.map((step, index) => (
                            <div key={index} className="border p-4 rounded-lg space-y-3" id={`work-step-${index}`}>
                              <div className="flex justify-between items-center">
                                <Label className="text-sm font-medium">Arbeitsschritt {index + 1}</Label>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => removeWorkStep(index)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Arbeitstyp</Label>
                                  <Select 
                                    value={step.step_type} 
                                    onValueChange={(value) => updateWorkStep(index, 'step_type', value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {workStepTypes.map((type) => (
                                        <SelectItem key={type} value={type}>
                                          {type}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Reihenfolge</Label>
                                  <Input
                                    type="number"
                                    value={step.order}
                                    onChange={(e) => updateWorkStep(index, 'order', parseInt(e.target.value))}
                                    min="1"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Notizen</Label>
                                <Textarea
                                  value={step.notes}
                                  onChange={(e) => updateWorkStep(index, 'notes', e.target.value)}
                                  placeholder="Zusätzliche Informationen für diesen Arbeitsschritt..."
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <Button type="submit" className="w-full">Erstellen</Button>
                    </form>
                  </DialogContent>
                </Dialog>

                {/* Edit Task Dialog */}
                <Dialog open={showEditTask} onOpenChange={setShowEditTask}>
                  <DialogContent className="max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Aufgabe bearbeiten</DialogTitle>
                      <DialogDescription>
                        Bearbeiten Sie die Aufgabendetails und den Arbeitsplan.
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={updateTask} className="space-y-4">
                      <div>
                        <Label htmlFor="edit_title">Auftrag</Label>
                        <Input
                          id="edit_title"
                          value={editTaskForm.title}
                          onChange={(e) => setEditTaskForm(prev => ({ ...prev, title: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="edit_work_instruction">Bezeichnung</Label>
                        <Textarea
                          id="edit_work_instruction"
                          value={editTaskForm.work_instruction}
                          onChange={(e) => setEditTaskForm(prev => ({ ...prev, work_instruction: e.target.value }))}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="edit_article_number">Artikelnummer</Label>
                        <Input
                          id="edit_article_number"
                          type="text"
                          value={editTaskForm.article_number}
                          onChange={(e) => setEditTaskForm(prev => ({ ...prev, article_number: e.target.value }))}
                          placeholder="Optional"
                        />
                      </div>
                      <div>
                        <Label>Liefertermin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start text-left font-normal">
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {format(editTaskForm.due_date, "PPP", { locale: de })}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0">
                            <Calendar
                              mode="single"
                              selected={editTaskForm.due_date}
                              onSelect={(date) => setEditTaskForm(prev => ({ ...prev, due_date: date }))}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label htmlFor="edit_target_quantity">Sollstückzahl</Label>
                        <Input
                          id="edit_target_quantity"
                          type="number"
                          value={editTaskForm.target_quantity}
                          onChange={(e) => setEditTaskForm(prev => ({ ...prev, target_quantity: parseInt(e.target.value) }))}
                          required
                        />
                      </div>
                      
                      {/* Work Plan Section */}
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <Label className="text-base font-medium">Arbeitsplan bearbeiten</Label>
                          <Button type="button" variant="outline" size="sm" onClick={addWorkStepToEdit}>
                            <PlusIcon className="h-4 w-4 mr-1" />
                            Arbeitsschritt hinzufügen
                          </Button>
                        </div>
                        
                        <div className="max-h-60 overflow-y-auto space-y-3">
                          {editTaskForm.work_steps.map((step, index) => (
                            <div key={index} className="border p-4 rounded-lg space-y-3 bg-yellow-50">
                              <div className="flex justify-between items-center">
                                <Label className="text-sm font-medium">Arbeitsschritt {index + 1}</Label>
                                <div className="flex space-x-2">
                                  {index > 0 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const newSteps = [...editTaskForm.work_steps];
                                        [newSteps[index], newSteps[index - 1]] = [newSteps[index - 1], newSteps[index]];
                                        setEditTaskForm(prev => ({ ...prev, work_steps: newSteps }));
                                      }}
                                    >
                                      ↑
                                    </Button>
                                  )}
                                  {index < editTaskForm.work_steps.length - 1 && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        const newSteps = [...editTaskForm.work_steps];
                                        [newSteps[index], newSteps[index + 1]] = [newSteps[index + 1], newSteps[index]];
                                        setEditTaskForm(prev => ({ ...prev, work_steps: newSteps }));
                                      }}
                                    >
                                      ↓
                                    </Button>
                                  )}
                                  <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => removeWorkStepFromEdit(index)}
                                    className="text-red-600 hover:text-red-700"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label>Arbeitstyp</Label>
                                  <Select 
                                    value={step.step_type} 
                                    onValueChange={(value) => updateWorkStepInEdit(index, 'step_type', value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {workStepTypes.map((type) => (
                                        <SelectItem key={type} value={type}>
                                          {type}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Reihenfolge</Label>
                                  <Input
                                    type="number"
                                    value={index + 1}
                                    disabled
                                    className="bg-gray-100"
                                  />
                                </div>
                              </div>
                              <div>
                                <Label>Notizen</Label>
                                <Textarea
                                  value={step.notes || ''}
                                  onChange={(e) => updateWorkStepInEdit(index, 'notes', e.target.value)}
                                  placeholder="Zusätzliche Informationen für diesen Arbeitsschritt..."
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {editTaskForm.work_steps.length === 0 && (
                          <div className="text-center py-4 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                            Keine Arbeitsschritte definiert. Klicken Sie "Arbeitsschritt hinzufügen" um zu beginnen.
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        <Button type="submit" className="flex-1">Änderungen speichern</Button>
                        <Button type="button" variant="outline" onClick={() => setShowEditTask(false)} className="flex-1">
                          Abbrechen
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
                </div>
                
                {/* Task Search - separate row under header */}
                <div className="flex justify-center">
                  <div className="relative max-w-2xl w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <SearchIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input
                      type="text"
                      placeholder="Aufgaben suchen (Auftrag, Bezeichnung, Artikelnummer, Status)..."
                      value={taskSearchTerm}
                      onChange={(e) => setTaskSearchTerm(e.target.value)}
                      className="pl-10 w-full"
                    />
                    {taskSearchTerm && (
                      <button
                        onClick={() => setTaskSearchTerm('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>

              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Auftrag</TableHead>
                        <TableHead>Bezeichnung</TableHead>
                        <TableHead>Artikelnummer</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Liefertermin</TableHead>
                        <TableHead>Sollstückzahl</TableHead>
                        <TableHead>Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredTasks().map((task) => (
                        <React.Fragment key={task.id}>
                          <TableRow>
                            <TableCell className="font-medium">
                              <div className="flex items-center">
                                {task.assigned_user_id && (
                                  <button
                                    onClick={() => toggleTaskEntries(task.id)}
                                    className="mr-2 p-1 hover:bg-gray-100 rounded"
                                  >
                                    {expandedTasks[task.id] ? (
                                      <ChevronDownIcon className="h-4 w-4" />
                                    ) : (
                                      <ChevronRightIcon className="h-4 w-4" />
                                    )}
                                  </button>
                                )}
                                {task.title}
                              </div>
                            </TableCell>
                            <TableCell>{task.work_instruction}</TableCell>
                            <TableCell>{task.article_number || '-'}</TableCell>
                            <TableCell>{getStatusBadge(task.status)}</TableCell>
                            <TableCell>{format(new Date(task.due_date), "PPP", { locale: de })}</TableCell>
                            <TableCell>{task.target_quantity}</TableCell>
                            <TableCell>
                              <div className="flex space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => editTask(task)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                >
                                  <PencilIcon className="h-4 w-4 mr-1" />
                                  Bearbeiten
                                </Button>
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <Button variant="outline" size="sm">
                                      Arbeitsschritte zuweisen
                                    </Button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-2xl">
                                    <DialogHeader>
                                      <DialogTitle>Arbeitsschritte zuweisen - {task.title}</DialogTitle>
                                      <DialogDescription>
                                        Weisen Sie die Arbeitsschritte dieser Aufgabe an Mitarbeiter zu.
                                      </DialogDescription>
                                    </DialogHeader>
                                    <WorkPlanAssignment 
                                      task={task} 
                                      users={users} 
                                      onAssign={assignWorkStep} 
                                      onRemove={removeWorkStepAssignment} 
                                      workStepTypes={workStepTypes}
                                      workStepAssignments={workStepAssignments}
                                    />
                                  </DialogContent>
                                </Dialog>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => archiveTask(task.id)}
                                  className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                >
                                  <FolderIcon className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => deleteTask(task.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expandable Time Details Row */}
                          {expandedTasks[task.id] && (
                            <TableRow>
                              <TableCell colSpan={5} className="bg-gray-50">
                                <div className="p-4">
                                  <div className="flex justify-between items-center mb-4">
                                    <h4 className="font-medium text-gray-900">Arbeitsschritt-Details</h4>
                                    <div className="flex space-x-2">
                                      <Button 
                                        variant="outline"
                                        size="sm" 
                                        onClick={() => fetchHistoricalTimeEntries(task.id)}
                                        className="text-blue-600 hover:text-blue-800"
                                      >
                                        <ClockIcon className="h-4 w-4 mr-1" />
                                        Alle Zeiteinträge
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => toggleTaskDetails(task.id)}
                                        className="text-gray-500"
                                      >
                                        <ChevronUpIcon className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  <TaskTimeDetailsContent taskId={task.id} timeDetails={taskTimeDetails[task.id]} />
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                          
                          {/* Toggle Row */}
                          <TableRow className="hover:bg-gray-50">
                            <TableCell colSpan={5} className="py-2 text-center">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => toggleTaskDetails(task.id)}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                {expandedTasks[task.id] ? (
                                  <>
                                    <ChevronUpIcon className="h-4 w-4 mr-1" />
                                    Zeitdetails ausblenden
                                  </>
                                ) : (
                                  <>
                                    <ChevronDownIcon className="h-4 w-4 mr-1" />
                                    Zeitdetails anzeigen
                                  </>
                                )}
                              </Button>
                            </TableCell>
                          </TableRow>
                          {expandedTasks[task.id] && taskEntries[task.id] && (
                            <TableRow>
                              <TableCell colSpan={6} className="bg-gray-50">
                                <div className="py-4">
                                  <h4 className="font-medium mb-3 text-gray-700">
                                    Zeiteinträge für Aufgabe "{task.title}":
                                  </h4>
                                  {taskEntries[task.id].length > 0 ? (
                                    <div className="space-y-2">
                                      {taskEntries[task.id].map((entry) => (
                                        <div key={entry.id} className="bg-white p-3 rounded border text-sm">
                                          <div className="flex justify-between items-start mb-2">
                                            <span className="font-medium text-blue-600">
                                              Eingetragen von: {entry.user_name || 'Unbekannter Benutzer'}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                              {format(new Date(entry.created_at), "PPP HH:mm", { locale: de })}
                                            </span>
                                          </div>
                                          <div className="grid grid-cols-5 gap-4">
                                            <div>
                                              <span className="font-medium text-gray-600">Datum:</span>
                                              <br />
                                              {format(new Date(entry.entry_date), "PPP", { locale: de })}
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-600">Zeit:</span>
                                              <br />
                                              {format(new Date(entry.start_time), "HH:mm")} - {format(new Date(entry.end_time), "HH:mm")}
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-600">Arbeitszeit:</span>
                                              <br />
                                              <span className="font-semibold text-blue-600">
                                                {Math.round((new Date(entry.end_time) - new Date(entry.start_time)) / (1000 * 60))} Min
                                              </span>
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-600">Ist-Stückzahl:</span>
                                              <br />
                                              {entry.actual_quantity}
                                            </div>
                                            <div>
                                              <span className="font-medium text-gray-600">Notizen:</span>
                                              <br />
                                              {entry.notes || 'Keine Notizen'}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-gray-500 italic">Keine Zeiteinträge vorhanden</p>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              </div>
            </TabsContent>

            <TabsContent value="archive" className="space-y-4">
              <ArchiveTable 
                archivedTasks={archivedTasks}
                onRestoreTask={restoreTaskFromArchive}
                onDeleteTask={deleteTask}
              />
            </TabsContent>

            <TabsContent value="assignments" className="space-y-4">
              <AssignmentTable 
                assignments={workStepAssignments}
                onEditAssignment={openEditAssignmentDialog}
                onCompleteAssignment={completeWorkStep}
                onRemoveAssignment={removeWorkStepAssignment}
                getStatusBadge={getStatusBadge}
              />
            </TabsContent>

            <TabsContent value="users" className="space-y-4">
              <UserTable 
                users={users}
                onCreateUser={createUser}
                onEditUser={editUser}
                onDeleteUser={deleteUser}
                onDeleteOldEntries={deleteOldEntries}
                onToggleUserActivities={toggleUserActivities}
                expandedUsers={expandedUsers}
                userActivities={userActivities}
              />
            </TabsContent>
          </Tabs>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-col space-y-4 md:flex-row md:justify-between md:items-center md:space-y-0">
              <h2 className="text-2xl font-bold text-gray-900">Meine Arbeitsschritte</h2>
              
              {/* Search functionality */}
              <div className="relative max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <SearchIcon className="h-5 w-5 text-gray-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Nach Aufgaben suchen (Auftrag, Bezeichnung, Artikelnummer, Arbeitsschritt)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
            
            <div className="grid gap-6">
              {getFilteredWorkStepAssignments().length > 0 ? (
                getFilteredWorkStepAssignments().map((assignment) => (
                <Card key={assignment.id} className={`w-full ${getWorkStepCardClass(assignment)}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="mb-2">{assignment.task?.title || 'Unbekannte Aufgabe'}</CardTitle>
                        <div className="mb-3">
                          <Badge 
                            variant="default" 
                            className={`text-lg px-4 py-2 font-semibold ${getWorkStepBadgeClass(assignment.work_step?.step_type)}`}
                          >
                            {assignment.work_step?.step_type || 'Unbekannter Schritt'}
                          </Badge>
                        </div>
                        <CardDescription>
                          {assignment.task?.work_instruction}
                        </CardDescription>
                      </div>
                      <div className="ml-4 flex flex-col items-end gap-2">
                        {getStatusBadge(assignment.status)}
                        {/* Show start status indicator */}
                        {assignment.status === 'Zugewiesen' && (
                          canStartWorkStep(assignment) ? (
                            <Badge className="bg-green-600 text-white text-xs">
                              ✓ Kann gestartet werden
                            </Badge>
                          ) : (
                            <Badge className="bg-red-600 text-white text-xs">
                              ⏳ Vorheriger Schritt nicht abgeschlossen
                            </Badge>
                          )
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Artikelnummer</Label>
                        <p className="text-sm">{assignment.task?.article_number || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Liefertermin</Label>
                        <p className="text-sm">
                          {assignment.task?.due_date ? 
                            format(new Date(assignment.task.due_date), "PPP", { locale: de }) : 
                            'Nicht gesetzt'
                          }
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-600">Sollstückzahl</Label>
                        <p className="text-sm">{assignment.task?.target_quantity || 'Nicht gesetzt'}</p>
                      </div>
                    </div>
                    
                    {assignment.work_step?.notes && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                        <Label className="text-sm font-medium text-blue-800">🔧 Arbeitsschritt-Hinweise</Label>
                        <p className="text-sm text-blue-700 font-medium mt-1">{assignment.work_step.notes}</p>
                      </div>
                    )}
                    
                    {(() => {
                      const nextStep = getNextWorkStep(assignment);
                      if (nextStep) {
                        return (
                          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                            <Label className="text-sm font-medium text-blue-800">Nächster Arbeitsschritt</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="bg-blue-100 text-blue-700 border-blue-300">
                                Schritt {nextStep.order}: {nextStep.step_type}
                              </Badge>
                              {nextStep.is_assigned ? (
                                nextStep.assigned_user?.id !== assignment.user_id ? (
                                  <span className="text-xs text-blue-600">
                                    (Zugewiesen an: {nextStep.assigned_user?.full_name || 'Anderer Benutzer'})
                                  </span>
                                ) : (
                                  <span className="text-xs text-green-600">
                                    (Auch Ihnen zugewiesen)
                                  </span>
                                )
                              ) : (
                                <span className="text-xs text-orange-600 font-medium">
                                  (Noch nicht zugewiesen)
                                </span>
                              )}
                            </div>
                            {nextStep.notes && (
                              <p className="text-xs text-blue-600 mt-1">
                                Hinweise: {nextStep.notes}
                              </p>
                            )}
                          </div>
                        );
                      } else {
                        return (
                          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                            <Label className="text-sm font-medium text-green-800">Workflow-Status</Label>
                            <p className="text-sm text-green-700 mt-1">
                              Dies ist der letzte Arbeitsschritt für diese Aufgabe
                            </p>
                          </div>
                        );
                      }
                    })()}
                    
                    <div className="flex space-x-2">
                      {assignment.status === 'Zugewiesen' && (
                        <WorkStepTimeTracker 
                          assignment={assignment}
                          onStart={startWorkStep}
                          onPause={pauseWorkStep}
                          onComplete={completeWorkStep}
                          onCompleteDirect={completeWorkStepDirect}
                          onAddDirectTime={addDirectTimeEntry}
                          onFetchTimeEntries={fetchWorkTimeEntries}
                          hasActiveWorkStep={getUserActiveWorkStep(user.id)}
                          canStart={canStartWorkStep(assignment)}
                        />
                      )}
                      
                      {assignment.status === 'In Bearbeitung' && (
                        <WorkStepTimeTracker 
                          assignment={assignment}
                          onStart={startWorkStep}
                          onPause={pauseWorkStep}
                          onComplete={completeWorkStep}
                          onCompleteDirect={completeWorkStepDirect}
                          onAddDirectTime={addDirectTimeEntry}
                          onFetchTimeEntries={fetchWorkTimeEntries}
                          hasActiveWorkStep={getUserActiveWorkStep(user.id)}
                          canStart={true}
                        />
                      )}
                      
                      {assignment.status === 'Pausiert' && (
                        <WorkStepTimeTracker 
                          assignment={assignment}
                          onStart={startWorkStep}
                          onPause={pauseWorkStep}
                          onComplete={completeWorkStep}
                          onFetchTimeEntries={fetchWorkTimeEntries}
                          hasActiveWorkStep={getUserActiveWorkStep(user.id)}
                          canStart={true}
                        />
                      )}
                      
                      {assignment.completed_at && (
                        <WorkStepTimeTracker 
                          assignment={assignment}
                          onStart={startWorkStep}
                          onPause={pauseWorkStep}
                          onComplete={completeWorkStep}
                          onFetchTimeEntries={fetchWorkTimeEntries}
                          canStart={false}
                        />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
              ) : (
                <Card>
                  <CardContent className="text-center py-8">
                    {searchTerm ? (
                      <div>
                        <p className="text-gray-500 mb-2">Keine Arbeitsschritte gefunden für:</p>
                        <p className="text-gray-700 font-medium">"{searchTerm}"</p>
                        <button
                          onClick={() => setSearchTerm('')}
                          className="mt-2 text-blue-600 hover:text-blue-800 underline"
                        >
                          Suche zurücksetzen
                        </button>
                      </div>
                    ) : (
                      <p className="text-gray-500">Keine Arbeitsschritte zugewiesen</p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Historical Time Entries Dialog */}
      <Dialog open={showHistoricalDialog} onOpenChange={setShowHistoricalDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alle Zeiteinträge für Aufgabe</DialogTitle>
            <DialogDescription>
              Vollständige Historie aller Zeiteinträge für diese Aufgabe
            </DialogDescription>
          </DialogHeader>
          
          {selectedTaskForHistorical && historicalTimeEntries[selectedTaskForHistorical] ? (
            <div className="space-y-4">
              {historicalTimeEntries[selectedTaskForHistorical].length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Keine Zeiteinträge für diese Aufgabe gefunden.
                </div>
              ) : (
                <div className="space-y-6">
                  {historicalTimeEntries[selectedTaskForHistorical].map((workStep, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-lg">
                          {workStep.step_type} - {workStep.user_name}
                        </h4>
                        <Badge className={workStep.status === 'Erledigt' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                          {workStep.status}
                        </Badge>
                      </div>
                      
                      {workStep.time_entries && workStep.time_entries.length > 0 ? (
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Datum</TableHead>
                                <TableHead>Startzeit</TableHead>
                                <TableHead>Endzeit</TableHead>
                                <TableHead>Arbeitszeit</TableHead>
                                {workStep.step_type?.toLowerCase() === 'lasern' && (
                                  <TableHead>🔧 Rüstzeit</TableHead>
                                )}
                                <TableHead>Notizen</TableHead>
                                {user?.role === 'admin' && <TableHead>Aktionen</TableHead>}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {workStep.time_entries.map((entry, entryIndex) => (
                                <TableRow key={entryIndex}>
                                  <TableCell>
                                    {format(new Date(entry.start_time), "dd.MM.yyyy", { locale: de })}
                                  </TableCell>
                                  <TableCell>
                                    {format(new Date(entry.start_time), "HH:mm", { locale: de })}
                                  </TableCell>
                                  <TableCell>
                                    {entry.end_time ? format(new Date(entry.end_time), "HH:mm", { locale: de }) : '-'}
                                  </TableCell>
                                  <TableCell>
                                    {entry.duration_minutes ? `${Math.round(entry.duration_minutes)} min` : '-'}
                                  </TableCell>
                                  {workStep.step_type?.toLowerCase() === 'lasern' && (
                                    <TableCell>
                                      {entry.setup_time_minutes ? (
                                        <span className="text-green-700 font-medium">
                                          {entry.setup_time_minutes} min
                                        </span>
                                      ) : '-'}
                                    </TableCell>
                                  )}
                                  <TableCell>
                                    {entry.notes || '-'}
                                  </TableCell>
                                  {user?.role === 'admin' && (
                                    <TableCell>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openEditTimeEntryDialog(entry)}
                                        className="text-blue-600 hover:text-blue-700"
                                      >
                                        <PencilIcon className="h-4 w-4 mr-1" />
                                        Bearbeiten
                                      </Button>
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          
                          <div className="mt-3 text-sm text-gray-600 font-medium">
                            Gesamtzeit: {workStep.total_minutes ? `${Math.round(workStep.total_minutes)} min` : '0 min'}
                            {workStep.step_type?.toLowerCase() === 'lasern' && workStep.time_entries.some(e => e.setup_time_minutes) && (
                              <span className="ml-4 text-green-700">
                                | Gesamt-Rüstzeit: {workStep.time_entries.reduce((sum, e) => sum + (e.setup_time_minutes || 0), 0)} min
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-gray-500 text-sm">
                          Keine Zeiteinträge für diesen Arbeitsschritt.
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Lade Zeiteinträge...</p>
            </div>
          )}
          
          <div className="flex justify-end mt-6">
            <Button variant="outline" onClick={() => setShowHistoricalDialog(false)}>
              Schließen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Assignment Dialog */}
      <Dialog open={showEditAssignmentDialog} onOpenChange={setShowEditAssignmentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Arbeitsschritt-Zuweisung bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie die Details der Arbeitsschritt-Zuweisung für {selectedAssignmentForEdit?.user?.full_name}
            </DialogDescription>
          </DialogHeader>
          {selectedAssignmentForEdit && (
            <form onSubmit={handleEditAssignmentSubmit} className="space-y-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Zuweisungs-Information</h4>
                <p className="text-sm text-blue-700">
                  <strong>Aufgabe:</strong> {selectedAssignmentForEdit.task?.title}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Arbeitsschritt:</strong> {selectedAssignmentForEdit.work_step?.step_type}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Zugewiesen an:</strong> {selectedAssignmentForEdit.user?.full_name}
                </p>
                <p className="text-sm text-blue-700">
                  <strong>Aktueller Status:</strong> <Badge variant="secondary">{selectedAssignmentForEdit.status}</Badge>
                </p>
              </div>

              <div>
                <Label htmlFor="edit_status">Status</Label>
                <select
                  id="edit_status"
                  value={editAssignmentForm.status}
                  onChange={(e) => setEditAssignmentForm(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Zugewiesen">Zugewiesen</option>
                  <option value="In Bearbeitung">In Bearbeitung</option>
                  <option value="Erledigt">Erledigt</option>
                </select>
              </div>

              <div>
                <Label htmlFor="edit_notes">Zuweisungsnotizen</Label>
                <Textarea
                  id="edit_notes"
                  value={editAssignmentForm.notes}
                  onChange={(e) => setEditAssignmentForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notizen zur Zuweisung..."
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="edit_completion_notes">Abschluss-Notizen</Label>
                <Textarea
                  id="edit_completion_notes"
                  value={editAssignmentForm.completion_notes}
                  onChange={(e) => setEditAssignmentForm(prev => ({ ...prev, completion_notes: e.target.value }))}
                  placeholder="Notizen zum Abschluss des Arbeitsschritts..."
                  rows={2}
                />
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                  Änderungen speichern
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditAssignmentDialog(false)}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Edit Time Entry Dialog */}
      <Dialog open={showEditTimeEntryDialog} onOpenChange={setShowEditTimeEntryDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Zeiteintrag bearbeiten</DialogTitle>
            <DialogDescription>
              Bearbeiten Sie die Details des Zeiteintrags
            </DialogDescription>
          </DialogHeader>
          {selectedTimeEntryForEdit && (
            <form onSubmit={handleEditTimeEntrySubmit} className="space-y-4">
              <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                <h4 className="font-medium text-yellow-900 mb-2">Zeiteintrag-Information</h4>
                <p className="text-sm text-yellow-700">
                  <strong>Datum:</strong> {format(new Date(selectedTimeEntryForEdit.start_time), "PPP", { locale: de })}
                </p>
                <p className="text-sm text-yellow-700">
                  <strong>Typ:</strong> {selectedTimeEntryForEdit.entry_type || 'work_session'}
                </p>
                {selectedTimeEntryForEdit.duration_minutes && (
                  <p className="text-sm text-yellow-700">
                    <strong>Aktuelle Arbeitszeit:</strong> {Math.round(selectedTimeEntryForEdit.duration_minutes)} Minuten
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_start_time">Startzeit</Label>
                  <Input
                    id="edit_start_time"
                    type="time"
                    value={editTimeEntryForm.start_time}
                    onChange={(e) => setEditTimeEntryForm(prev => ({ ...prev, start_time: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="edit_end_time">Endzeit</Label>
                  <Input
                    id="edit_end_time"
                    type="time"
                    value={editTimeEntryForm.end_time}
                    onChange={(e) => setEditTimeEntryForm(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="edit_actual_quantity">Ist-Stückzahl</Label>
                <Input
                  id="edit_actual_quantity"
                  type="number"
                  min="0"
                  value={editTimeEntryForm.actual_quantity}
                  onChange={(e) => setEditTimeEntryForm(prev => ({ ...prev, actual_quantity: e.target.value }))}
                  placeholder="Anzahl bearbeiteter Stücke"
                />
              </div>

              <div>
                <Label htmlFor="edit_setup_time_minutes">🔧 Rüstzeit (in 10-Minuten-Schritten)</Label>
                <select
                  id="edit_setup_time_minutes"
                  value={editTimeEntryForm.setup_time_minutes}
                  onChange={(e) => setEditTimeEntryForm(prev => ({ ...prev, setup_time_minutes: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 bg-white"
                >
                  <option value="0">Keine Rüstzeit (0 min)</option>
                  <option value="10">10 Minuten</option>
                  <option value="20">20 Minuten</option>
                  <option value="30">30 Minuten</option>
                  <option value="40">40 Minuten</option>
                  <option value="50">50 Minuten</option>
                  <option value="60">1 Stunde (60 min)</option>
                  <option value="70">1 Stunde 10 Minuten</option>
                  <option value="80">1 Stunde 20 Minuten</option>
                  <option value="90">1 Stunde 30 Minuten</option>
                  <option value="100">1 Stunde 40 Minuten</option>
                  <option value="110">1 Stunde 50 Minuten</option>
                  <option value="120">2 Stunden (120 min)</option>
                  <option value="130">2 Stunden 10 Minuten</option>
                  <option value="140">2 Stunden 20 Minuten</option>
                  <option value="150">2 Stunden 30 Minuten</option>
                  <option value="160">2 Stunden 40 Minuten</option>
                  <option value="170">2 Stunden 50 Minuten</option>
                  <option value="180">3 Stunden (180 min)</option>
                </select>
                {editTimeEntryForm.setup_time_minutes !== '0' && (
                  <div className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded mt-2">
                    <strong>Ausgewählte Rüstzeit:</strong> {editTimeEntryForm.setup_time_minutes} Minuten
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="edit_work_notes">Arbeitsnotizen</Label>
                <Textarea
                  id="edit_work_notes"
                  value={editTimeEntryForm.work_notes}
                  onChange={(e) => setEditTimeEntryForm(prev => ({ ...prev, work_notes: e.target.value }))}
                  placeholder="Beschreibung der durchgeführten Arbeiten..."
                  rows={3}
                />
              </div>

              <div className="flex space-x-2">
                <Button type="submit" className="flex-1 bg-yellow-600 hover:bg-yellow-700">
                  Zeiteintrag aktualisieren
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowEditTimeEntryDialog(false)}
                  className="flex-1"
                >
                  Abbrechen
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Toaster />
    </div>
  );
}

// Work Plan Assignment Component
function WorkPlanAssignment({ task, users, onAssign, onRemove, workStepTypes, workStepAssignments = [] }) {
  const [workPlan, setWorkPlan] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWorkPlan();
  }, [task.id]);

  // Update assignments when workStepAssignments prop changes
  useEffect(() => {
    if (workStepAssignments.length > 0) {
      const taskAssignments = workStepAssignments.filter(assignment => 
        assignment.task_id === task.id
      );
      setAssignments(taskAssignments);
    }
  }, [workStepAssignments, task.id]);

  const fetchWorkPlan = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch work plan
      const workPlanResponse = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tasks/${task.id}/work-plan`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkPlan(workPlanResponse.data);
      setError(null);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Kein Arbeitsplan für diese Aufgabe gefunden');
      } else {
        setError('Fehler beim Laden des Arbeitsplans');
      }
      setWorkPlan(null);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (stepId, userId) => {
    // Check if this work step is already assigned
    const existingAssignment = assignments.find(assignment => 
      assignment.work_step_id === stepId
    );
    
    if (existingAssignment) {
      alert(`Dieser Arbeitsschritt ist bereits an ${existingAssignment.user?.full_name || 'einen Mitarbeiter'} zugewiesen.`);
      return;
    }
    
    // Proceed with assignment
    await onAssign(stepId, userId);
    
    // The assignments will be updated via the useEffect hook when workStepAssignments prop changes
  };

  const handleRemove = async (assignmentId) => {
    await onRemove(assignmentId);
    
    // The assignments will be updated via the useEffect hook when workStepAssignments prop changes
  };

  const getAssignedUser = (stepId) => {
    const assignment = assignments.find(assignment => 
      assignment.work_step_id === stepId
    );
    return assignment ? assignment.user : null;
  };

  const getAssignmentId = (stepId) => {
    const assignment = assignments.find(assignment => 
      assignment.work_step_id === stepId
    );
    return assignment ? assignment.id : null;
  };

  if (loading) {
    return <div className="p-4 text-center">Lade Arbeitsplan...</div>;
  }

  if (error) {
    return <div className="p-4 text-center text-gray-500">{error}</div>;
  }

  if (!workPlan || !workPlan.work_steps || workPlan.work_steps.length === 0) {
    return <div className="p-4 text-center text-gray-500">Keine Arbeitsschritte in diesem Arbeitsplan</div>;
  }

  return (
    <div className="space-y-4">
      {workPlan.work_steps
        .sort((a, b) => a.order - b.order)
        .map((step) => {
          const assignedUser = getAssignedUser(step.id);
          const assignmentId = getAssignmentId(step.id);
          return (
            <div key={step.id} className="border p-4 rounded-lg">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-medium">
                    Schritt {step.order}: {step.step_type}
                  </h4>
                  {step.notes && (
                    <p className="text-sm text-gray-600 mt-1">{step.notes}</p>
                  )}
                </div>
                <Badge variant="secondary">{step.status}</Badge>
              </div>
              
              <div className="flex items-center space-x-2">
                <Label className="text-sm">
                  {assignedUser ? 'Zugewiesen an:' : 'Zuweisen an:'}
                </Label>
                {assignedUser ? (
                  <div className="flex items-center space-x-2">
                    <Badge variant="default" className="bg-green-100 text-green-800">
                      {assignedUser.full_name}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      (bereits zugewiesen)
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (assignmentId && window.confirm(`Zuweisung von ${assignedUser.full_name} wirklich entfernen?`)) {
                          handleRemove(assignmentId);
                        }
                      }}
                      className="text-red-600 hover:text-red-700 ml-2"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Select onValueChange={(userId) => handleAssign(step.id, userId)}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Mitarbeiter auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.role === 'user').map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
}

// Work Step Time Tracker Component
function TaskTimeDetailsContent({ taskId, timeDetails }) {
  if (!timeDetails) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-500">Lade Zeitdetails...</p>
      </div>
    );
  }

  if (timeDetails.error) {
    return (
      <div className="text-center py-4">
        <p className="text-red-500">{timeDetails.error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Task Summary */}
      <div className="bg-blue-50 p-3 rounded-lg">
        <h5 className="font-medium text-blue-900 mb-2">Auftrags-Übersicht</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <span className="text-blue-700 font-medium">Liefertermin:</span>
            <p>{format(new Date(timeDetails.task.due_date), "PPP", { locale: de })}</p>
          </div>
          <div>
            <span className="text-blue-700 font-medium">Sollstückzahl:</span>
            <p>{timeDetails.task.target_quantity}</p>
          </div>
          <div>
            <span className="text-blue-700 font-medium">Gesamtzeit:</span>
            <p className="text-green-600 font-medium">{timeDetails.total_time_minutes} Min</p>
          </div>
          <div>
            <span className="text-blue-700 font-medium">Sitzungen:</span>
            <p>{timeDetails.total_sessions}</p>
          </div>
        </div>
      </div>

      {/* Work Steps */}
      {timeDetails.work_step_summaries.length === 0 ? (
        <div className="text-center py-4 text-gray-500">
          <p>Keine Arbeitsschritte oder Zeiterfassungen gefunden</p>
        </div>
      ) : (
        <div className="space-y-3">
          <h5 className="font-medium text-gray-900">Arbeitsschritte:</h5>
          {timeDetails.work_step_summaries.map((summary, index) => (
            <div key={index} className="border rounded-lg p-3 bg-white">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h6 className="font-medium">
                    Schritt {summary.work_step?.order || 'N/A'}: {summary.work_step?.step_type || 'Unbekannter Arbeitsschritt'}
                  </h6>
                  <p className="text-sm text-gray-600">
                    Zugewiesen an: <span className="font-medium">{summary.user?.full_name || 'Unbekannter Benutzer'}</span>
                  </p>
                  {summary.work_step?.notes && (
                    <p className="text-sm text-gray-600 mt-1">
                      Hinweise: {summary.work_step.notes}
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <Badge variant={summary.assignment.status === 'Erledigt' ? 'default' : 'secondary'}>
                    {summary.assignment.status}
                  </Badge>
                  <p className="text-sm text-gray-600 mt-1">
                    <strong>{summary.time_minutes} Min</strong>
                  </p>
                  <p className="text-xs text-gray-500">
                    {summary.session_count} Sitzungen
                  </p>
                </div>
              </div>

              {/* Time Entries */}
              {summary.time_entries.length > 0 && (
                <div className="mt-3">
                  <h6 className="text-sm font-medium mb-2">Arbeitssitzungen:</h6>
                  <div className="space-y-2">
                    {summary.time_entries.map((entry, entryIndex) => (
                      <div key={entry.id} className="bg-gray-50 p-2 rounded text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">
                              Sitzung {entryIndex + 1}: {format(new Date(entry.start_time), "HH:mm")} - {entry.end_time ? format(new Date(entry.end_time), "HH:mm") : 'Laufend'}
                            </p>
                            {entry.end_time && (
                              <p className="text-gray-600">
                                Dauer: {Math.round((new Date(entry.end_time) - new Date(entry.start_time)) / (1000 * 60))} Min
                              </p>
                            )}
                            {entry.actual_quantity && (
                              <p className="text-gray-600">Stückzahl: {entry.actual_quantity}</p>
                            )}
                            {entry.work_notes && (
                              <p className="text-gray-600 mt-1">Notizen: {entry.work_notes}</p>
                            )}
                          </div>
                          <Badge variant={entry.entry_type === 'final_completion' ? 'default' : 'secondary'} className="ml-2">
                            {entry.entry_type === 'final_completion' ? 'Final' : 'Pause'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {summary.assignment.completion_notes && (
                <div className="mt-2 p-2 bg-green-50 rounded">
                  <p className="text-sm">
                    <strong className="text-green-900">Abschluss-Notizen:</strong>
                    <span className="text-green-700 ml-2">{summary.assignment.completion_notes}</span>
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default App;
